import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Lock, Heart, MessageSquare, Mail as MailIcon, LogOut, ShieldCheck,
  CalendarClock, FileText, Plus, Edit3, Search, Star, Youtube,
  CheckCircle2, Archive, Clock, Filter, Image as ImageIcon, Sparkles, Upload as UploadIcon,
} from "lucide-react";
import DevotionalEditor from "@/components/admin/DevotionalEditor";
import VideoEditor from "@/components/admin/VideoEditor";
import MediaLibrary from "@/components/admin/MediaLibrary";
import AIPrayerResponse from "@/components/admin/AIPrayerResponse";
import BulkImport from "@/components/admin/BulkImport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "abide_admin_token";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const fmtDateOnly = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};
const decodeEntities = (s = "") =>
  s.replace(/&#8217;/g, "’").replace(/&#8216;/g, "‘").replace(/&#038;/g, "&").replace(/&amp;/g, "&")
    .replace(/&#8211;/g, "–").replace(/&#8212;/g, "—").replace(/&quot;/g, "\"").replace(/&#39;/g, "'");

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [tab, setTab] = useState("devotionals");
  const [error, setError] = useState("");

  const [prayers, setPrayers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [videos, setVideos] = useState([]);
  const [migration, setMigration] = useState(null);

  const [editorPost, setEditorPost] = useState(null);   // post object OR {} for new
  const [editorVideo, setEditorVideo] = useState(null); // video OR {}
  const [aiPrayer, setAiPrayer] = useState(null);       // prayer object for AI response modal
  const [emailTest, setEmailTest] = useState({ state: "idle", message: "" });

  // Filters
  const [postSearch, setPostSearch] = useState("");
  const [postStatusFilter, setPostStatusFilter] = useState("all");
  const [prayerStatusFilter, setPrayerStatusFilter] = useState("all");

  const reload = useCallback(async (t) => {
    setError("");
    const headers = { "X-Admin-Token": t || token };
    try {
      const [p, c, s, allPosts, cats, tagsR, vids, mig] = await Promise.all([
        axios.get(`${API}/prayer-requests`, { headers }),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/subscribers`, { headers }),
        axios.get(`${API}/admin/posts`, { headers }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/tags`),
        axios.get(`${API}/videos`),
        axios.get(`${API}/migration/report`, { headers }),
      ]);
      setPrayers(p.data || []);
      setContacts(c.data || []);
      setSubs(s.data || []);
      setPosts(allPosts.data || []);
      setCategories(cats.data || []);
      setTags(tagsR.data || []);
      setVideos(vids.data || []);
      setMigration(mig.data || null);
    } catch (e) {
      setError("Unauthorized or server error. Check your admin token.");
      setToken("");
      localStorage.removeItem(TOKEN_KEY);
    }
  }, [token]);

  useEffect(() => { if (token) reload(token); }, [token, reload]);

  // ---------- Prayer request status update ----------
  const updatePrayerStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/prayer-requests/${id}`, { status }, { headers: { "X-Admin-Token": token } });
      setPrayers((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) { setError("Failed to update status."); }
  };

  // ---------- Quick "publish now" / "unpublish" toggles for posts ----------
  const togglePostStatus = async (post) => {
    const newStatus = post.status === "publish" ? "draft" : "publish";
    try {
      await axios.put(
        `${API}/admin/posts/${post.id || post.slug}`,
        { status: newStatus, published_at: post.published_at },
        { headers: { "X-Admin-Token": token } }
      );
      await reload();
    } catch (e) { setError("Failed to update status."); }
  };

  const setFeatured = async (post) => {
    try {
      await axios.put(
        `${API}/admin/posts/${post.id || post.slug}`,
        { is_featured: true },
        { headers: { "X-Admin-Token": token } }
      );
      await reload();
    } catch (e) { setError("Failed to feature post."); }
  };

  // ---------- Email test ----------
  const sendEmailTest = async () => {
    setEmailTest({ state: "loading", message: "" });
    try {
      const r = await axios.post(`${API}/admin/email/test`, {}, { headers: { "X-Admin-Token": token } });
      setEmailTest({ state: r.data.ok ? "success" : "error", message: r.data.message });
    } catch (e) {
      setEmailTest({ state: "error", message: e.response?.data?.detail || e.message });
    }
  };

  // ---------- Derived (must be before any early return so hooks order is stable) ----------
  const filteredPosts = useMemo(() => {
    let arr = posts;
    if (postStatusFilter !== "all") arr = arr.filter((p) => p.status === postStatusFilter);
    if (postSearch.trim()) {
      const q = postSearch.toLowerCase();
      arr = arr.filter((p) =>
        decodeEntities(p.title).toLowerCase().includes(q) ||
        (p.slug || "").toLowerCase().includes(q) ||
        (p.excerpt || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [posts, postStatusFilter, postSearch]);

  const filteredPrayers = useMemo(() => {
    if (prayerStatusFilter === "all") return prayers;
    return prayers.filter((p) => (p.status || "new") === prayerStatusFilter);
  }, [prayers, prayerStatusFilter]);

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5" style={{ backgroundColor: "var(--cream-50)" }}>
        <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-cream-200 shadow-xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: "var(--navy-800)", color: "var(--gold-400)" }}>
              <ShieldCheck size={22} />
            </span>
            <div>
              <h1 className="font-display text-2xl text-navy-deep">Ministry Admin</h1>
              <p className="text-xs text-navy/60">Abide Daily — internal access only</p>
            </div>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); localStorage.setItem(TOKEN_KEY, input); setToken(input); }}>
            <label className="block text-sm font-medium text-navy mb-2">Admin Token</label>
            <input data-testid="admin-token-input" type="password" value={input} onChange={(e) => setInput(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none mb-4"
              placeholder="Enter admin token" required />
            <button data-testid="admin-login" className="btn-primary w-full py-3 rounded-full inline-flex items-center justify-center gap-2">
              <Lock size={16} /> Enter
            </button>
            {error && <p className="text-red-600 text-sm mt-3" data-testid="admin-error">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  const counts = {
    publishedNow: posts.filter((p) => p.status === "publish" && p.published_at <= new Date().toISOString()).length,
    scheduled: posts.filter((p) => p.status === "future" || (p.status === "publish" && p.published_at > new Date().toISOString())).length,
    draft: posts.filter((p) => p.status === "draft").length,
  };

  const tabs = [
    { id: "devotionals", label: "Devotionals", count: posts.length, Icon: FileText },
    { id: "bulk", label: "Bulk Import", count: null, Icon: UploadIcon },
    { id: "videos", label: "Videos", count: videos.length, Icon: Youtube },
    { id: "media", label: "Media", count: null, Icon: ImageIcon },
    { id: "prayers", label: "Prayer Requests", count: prayers.length, Icon: Heart },
    { id: "contacts", label: "Contact Messages", count: contacts.length, Icon: MessageSquare },
    { id: "subs", label: "Subscribers", count: subs.length, Icon: MailIcon },
    { id: "scheduled", label: "Scheduled", count: counts.scheduled, Icon: CalendarClock },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--cream-50)" }} data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-cream-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "var(--navy-800)", color: "var(--gold-400)" }}>
              <ShieldCheck size={20} />
            </span>
            <div>
              <h1 className="font-display text-xl text-navy-deep">Abide Daily — Admin</h1>
              <p className="text-xs text-navy/55">Ministry content & submissions</p>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }}
            className="btn-outline-navy px-4 py-2 rounded-full text-sm inline-flex items-center gap-2">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Migration banner */}
        {migration && (
          <div className="mb-6 rounded-2xl p-5 border border-gold/30" style={{ background: "linear-gradient(135deg, var(--cream-100), var(--cream-50))" }} data-testid="migration-banner">
            <div className="flex flex-wrap items-center gap-6 justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Content Library</div>
                <p className="font-display text-xl text-navy-deep mt-1">All WordPress content imported & live</p>
              </div>
              <div className="flex flex-wrap gap-5 text-sm">
                <Stat label="Pages" v={migration.pages_published} />
                <Stat label="Published" v={counts.publishedNow} />
                <Stat label="Scheduled" v={counts.scheduled} highlight />
                <Stat label="Drafts" v={counts.draft} />
                <Stat label="Categories" v={migration.categories} />
                <Stat label="Tags" v={migration.tags} />
                <Stat label="Videos" v={videos.length} />
              </div>
            </div>
          </div>
        )}

        {/* Email delivery status banner */}
        {migration?.email && (
          <div data-testid="email-banner"
            className={`mb-6 rounded-2xl p-5 border flex flex-wrap items-center gap-4 justify-between ${
              migration.email.configured ? "border-green-200 bg-green-50" : "border-amber-300 bg-amber-50"
            }`}>
            <div className="flex items-start gap-3">
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                migration.email.configured ? "bg-green-600 text-white" : "bg-amber-500 text-white"
              }`}>
                <MailIcon size={18} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: migration.email.configured ? "#15803d" : "#a16207" }}>
                  {migration.email.configured ? "Email Delivery: Active" : "Email Delivery: Not Configured"}
                </div>
                <p className="text-sm text-navy-deep mt-0.5">
                  Notifications send to <strong>{migration.email.ministry_email}</strong>
                  {migration.email.configured ? " via " : " — currently MOCKED. Add "}
                  <code className="text-xs bg-white/70 px-1.5 py-0.5 rounded">RESEND_API_KEY</code>
                  {migration.email.configured ? " (Resend)." : " in /app/backend/.env to enable."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {emailTest.message && (
                <span data-testid="email-test-result" className={`text-sm font-medium ${emailTest.state === "success" ? "text-green-700" : "text-red-700"}`}>
                  {emailTest.message}
                </span>
              )}
              <button data-testid="email-test-btn" onClick={sendEmailTest} disabled={emailTest.state === "loading" || !migration.email.configured}
                className="btn-primary px-5 py-2 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50">
                <MailIcon size={14} /> {emailTest.state === "loading" ? "Sending..." : "Send Test Email"}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="admin-top-error">{error}</div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap mb-6" data-testid="admin-tabs">
          {tabs.map(({ id, label, count, Icon }) => (
            <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
              className={`px-4 py-2.5 rounded-full text-sm font-medium inline-flex items-center gap-2 transition ${
                tab === id ? "btn-primary" : "bg-white border border-cream-200 text-navy hover:border-gold"
              }`}>
              <Icon size={15} /> {label}{count !== null && <span className="opacity-70">({count})</span>}
            </button>
          ))}
        </div>

        {/* ---------- MEDIA LIBRARY ---------- */}
        {tab === "media" && <MediaLibrary token={token} />}

        {/* ---------- BULK IMPORT ---------- */}
        {tab === "bulk" && <BulkImport token={token} onImported={() => reload()} />}

        {/* ---------- DEVOTIONALS ---------- */}
        {tab === "devotionals" && (
          <section className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
            <div className="p-5 border-b border-cream-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" size={16} />
                  <input data-testid="devotional-search" value={postSearch} onChange={(e) => setPostSearch(e.target.value)}
                    placeholder="Search devotionals…"
                    className="pl-9 pr-4 py-2.5 rounded-full border border-cream-200 bg-cream-50 focus-gold focus:outline-none text-sm w-72" />
                </div>
                <select data-testid="devotional-status-filter" value={postStatusFilter} onChange={(e) => setPostStatusFilter(e.target.value)}
                  className="px-4 py-2.5 rounded-full border border-cream-200 bg-cream-50 focus-gold focus:outline-none text-sm">
                  <option value="all">All statuses</option>
                  <option value="publish">Published</option>
                  <option value="future">Scheduled</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <button data-testid="devotional-new" onClick={() => setEditorPost({})}
                className="btn-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                <Plus size={16} /> New Devotional
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-cream-50 border-b border-cream-200">
                  <tr>
                    <Th>Status</Th><Th>Date</Th><Th>Title</Th><Th>Categories</Th><Th>Featured</Th><Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-navy/55 py-12">No devotionals match the filters.</td></tr>
                  )}
                  {filteredPosts.map((p) => (
                    <tr key={p.id || p.slug} data-testid={`post-row-${p.slug}`} className="border-b border-cream-100 hover:bg-cream-50 align-top">
                      <td className="px-5 py-3"><StatusBadge status={p.status} publishedAt={p.published_at} /></td>
                      <td className="px-5 py-3 text-navy/70 whitespace-nowrap">{fmtDateOnly(p.published_at)}</td>
                      <td className="px-5 py-3">
                        <div className="font-medium text-navy-deep">{decodeEntities(p.title)}</div>
                        <code className="text-[0.7rem] text-navy/55">{p.slug}</code>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(p.category_names || []).slice(0, 3).map((c) => (
                            <span key={c} className="px-2 py-0.5 rounded-full text-[0.65rem] bg-cream-100 text-navy-deep">{decodeEntities(c)}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        {p.is_featured ? (
                          <span className="inline-flex items-center gap-1 text-gold text-sm font-semibold"><Star size={14} fill="currentColor" /> Featured</span>
                        ) : (
                          <button onClick={() => setFeatured(p)} data-testid={`set-featured-${p.slug}`}
                            className="text-navy/40 hover:text-gold inline-flex items-center gap-1 text-xs">
                            <Star size={14} /> Set
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <button data-testid={`edit-${p.slug}`} onClick={() => setEditorPost(p)}
                          className="inline-flex items-center gap-1 text-navy-deep hover:text-gold text-sm font-medium">
                          <Edit3 size={14} /> Edit
                        </button>
                        <button data-testid={`toggle-${p.slug}`} onClick={() => togglePostStatus(p)}
                          className="ml-3 text-xs text-navy/60 hover:text-navy-deep">
                          {p.status === "publish" ? "Unpublish" : "Publish"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ---------- VIDEOS ---------- */}
        {tab === "videos" && (
          <section className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
            <div className="p-5 border-b border-cream-200 flex items-center justify-between">
              <h3 className="font-display text-xl text-navy-deep">Video Ministry</h3>
              <button data-testid="video-new" onClick={() => setEditorVideo({})}
                className="btn-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
                <Plus size={16} /> Add Video
              </button>
            </div>
            <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.length === 0 && <p className="text-center text-navy/55 py-12 col-span-full">No videos yet. Add the first one!</p>}
              {videos.map((v) => (
                <div key={v.id} data-testid={`video-row-${v.id}`} className="lift bg-cream-50 border border-cream-200 rounded-xl overflow-hidden">
                  <div className="relative aspect-video bg-navy-deep">
                    {v.youtube_id ? (
                      <img src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-cream/60 text-xs uppercase tracking-wider">
                        No video ID yet
                      </div>
                    )}
                    {v.is_featured && (
                      <span className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-gold text-navy-deep text-[0.65rem] font-bold uppercase tracking-wider inline-flex items-center gap-1">
                        <Star size={10} fill="currentColor" /> Featured
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="font-display text-base text-navy-deep leading-snug">{v.title}</h4>
                    <p className="mt-1 text-xs text-navy/60 line-clamp-2">{v.description}</p>
                    <div className="mt-3 flex justify-between items-center">
                      <code className="text-[0.65rem] text-navy/40">{v.youtube_id || "—"}</code>
                      <button data-testid={`video-edit-${v.id}`} onClick={() => setEditorVideo(v)}
                        className="text-navy-deep hover:text-gold inline-flex items-center gap-1 text-xs font-medium">
                        <Edit3 size={12} /> Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ---------- PRAYER REQUESTS ---------- */}
        {tab === "prayers" && (
          <section className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
            <div className="p-5 border-b border-cream-200 flex flex-wrap items-center gap-3">
              <Filter size={16} className="text-navy/50" />
              <select data-testid="prayer-status-filter" value={prayerStatusFilter} onChange={(e) => setPrayerStatusFilter(e.target.value)}
                className="px-4 py-2 rounded-full border border-cream-200 bg-cream-50 focus-gold focus:outline-none text-sm">
                <option value="all">All ({prayers.length})</option>
                <option value="new">New ({prayers.filter((p) => (p.status || "new") === "new").length})</option>
                <option value="responded">Responded</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="divide-y divide-cream-100">
              {filteredPrayers.length === 0 && <p className="text-center text-navy/55 py-12">No prayer requests yet.</p>}
              {filteredPrayers.map((p) => (
                <article key={p.id} data-testid={`prayer-row-${p.id}`} className="p-5 hover:bg-cream-50">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-display text-lg text-navy-deep">{p.name}</span>
                        {p.email && <span className="text-xs text-navy/55">· {p.email}</span>}
                        <PrayerBadge status={p.status || "new"} />
                        {p.confidential && (
                          <span className="px-2 py-0.5 rounded-full text-[0.65rem] uppercase tracking-wider bg-gold/10 text-[var(--gold-600)] border border-gold/30">
                            Confidential
                          </span>
                        )}
                        {p.contact_me && (
                          <span className="px-2 py-0.5 rounded-full text-[0.65rem] uppercase tracking-wider bg-navy-800 text-cream">
                            Wants Contact
                          </span>
                        )}
                        {p.email_attempted_at && (
                          <span data-testid={`prayer-email-${p.id}`}
                            className={`px-2 py-0.5 rounded-full text-[0.65rem] uppercase tracking-wider inline-flex items-center gap-1 ${
                              p.email_delivered ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
                            }`}>
                            <MailIcon size={10} /> {p.email_delivered ? "Email Sent" : "Email Failed"}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-navy/50 mb-2">{fmtDate(p.created_at)}</p>
                      <p className="text-sm text-navy/85 whitespace-pre-wrap">{p.request}</p>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button data-testid={`prayer-ai-${p.id}`} onClick={() => setAiPrayer(p)}
                        className="text-xs px-3 py-1.5 rounded-full bg-navy-deep text-cream border border-navy-deep hover:opacity-90 inline-flex items-center gap-1.5">
                        <Sparkles size={12} /> Suggest Reply (AI)
                      </button>
                      <button data-testid={`prayer-respond-${p.id}`} onClick={() => updatePrayerStatus(p.id, "responded")}
                        className="text-xs px-3 py-1.5 rounded-full bg-green-50 text-green-800 border border-green-200 hover:bg-green-100 inline-flex items-center gap-1.5">
                        <CheckCircle2 size={12} /> Mark Responded
                      </button>
                      <button data-testid={`prayer-archive-${p.id}`} onClick={() => updatePrayerStatus(p.id, "archived")}
                        className="text-xs px-3 py-1.5 rounded-full bg-cream-50 text-navy/70 border border-cream-200 hover:bg-cream-100 inline-flex items-center gap-1.5">
                        <Archive size={12} /> Archive
                      </button>
                      {p.status && p.status !== "new" && (
                        <button onClick={() => updatePrayerStatus(p.id, "new")} className="text-xs px-3 py-1.5 rounded-full hover:bg-cream-100 text-navy/60">
                          Mark New
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ---------- CONTACTS ---------- */}
        {tab === "contacts" && (
          <Table empty="No contact messages." columns={["Date", "Name", "Email", "Subject", "Message"]}
            rows={contacts.map((c) => [
              fmtDate(c.created_at), c.name, c.email, c.subject || "—",
              <div key="msg" className="max-w-md whitespace-pre-wrap text-sm">{c.message}</div>,
            ])} />
        )}

        {/* ---------- SUBSCRIBERS ---------- */}
        {tab === "subs" && (
          <Table empty="No subscribers yet." columns={["Date", "Email", "Name"]}
            rows={subs.map((s) => [fmtDate(s.created_at), s.email, s.name || "—"])} />
        )}

        {/* ---------- SCHEDULED ---------- */}
        {tab === "scheduled" && (
          <section className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
            <div className="p-5 border-b border-cream-200">
              <h3 className="font-display text-xl text-navy-deep">Scheduled to Publish</h3>
              <p className="text-sm text-navy/60 mt-1">These devotionals will appear on the public site automatically at the scheduled time.</p>
            </div>
            <Table empty="Nothing scheduled."
              columns={["Will publish", "Title", "Slug", "Categories", "Actions"]}
              rows={posts.filter((p) => p.status === "future" || (p.status === "publish" && p.published_at > new Date().toISOString()))
                .sort((a, b) => a.published_at.localeCompare(b.published_at))
                .map((p) => [
                  <span key="d" className="inline-flex items-center gap-2 font-medium text-gold"><Clock size={14} /> {fmtDateOnly(p.published_at)}</span>,
                  decodeEntities(p.title),
                  <code key="s" className="text-xs text-navy/60">{p.slug}</code>,
                  <div key="c" className="flex flex-wrap gap-1">
                    {(p.category_names || []).map((c) => (
                      <span key={c} className="px-2 py-0.5 rounded-full text-[0.65rem] bg-cream-100 text-navy-deep">{decodeEntities(c)}</span>
                    ))}
                  </div>,
                  <button key="e" data-testid={`scheduled-edit-${p.slug}`} onClick={() => setEditorPost(p)}
                    className="text-navy-deep hover:text-gold inline-flex items-center gap-1 text-sm font-medium">
                    <Edit3 size={12} /> Edit
                  </button>,
                ])} />
          </section>
        )}
      </div>

      {/* Editors */}
      {editorPost !== null && (
        <DevotionalEditor
          post={editorPost}
          categories={categories}
          tags={tags}
          token={token}
          onClose={() => setEditorPost(null)}
          onSaved={() => reload()}
        />
      )}
      {editorVideo !== null && (
        <VideoEditor
          video={editorVideo}
          token={token}
          onClose={() => setEditorVideo(null)}
          onSaved={() => reload()}
        />
      )}
      {aiPrayer !== null && (
        <AIPrayerResponse
          prayer={aiPrayer}
          token={token}
          onClose={() => setAiPrayer(null)}
        />
      )}
    </div>
  );
}

function Stat({ label, v, highlight }) {
  return (
    <div className="flex flex-col items-center">
      <span className={`font-display text-2xl ${highlight ? "text-gold" : "text-navy-deep"}`}>{v}</span>
      <span className="text-[0.65rem] uppercase tracking-wider text-navy/55 mt-0.5">{label}</span>
    </div>
  );
}

function StatusBadge({ status, publishedAt }) {
  const nowIso = new Date().toISOString();
  let effective = status;
  if (status === "publish" && publishedAt > nowIso) effective = "future";
  const map = {
    publish: { bg: "bg-green-100", color: "text-green-800", label: "Published" },
    future: { bg: "bg-gold/15", color: "text-[var(--gold-600)]", label: "Scheduled" },
    draft: { bg: "bg-cream-100", color: "text-navy/70", label: "Draft" },
  };
  const m = map[effective] || { bg: "bg-cream-100", color: "text-navy/70", label: effective };
  return <span className={`px-2.5 py-1 rounded-full text-[0.65rem] uppercase font-semibold tracking-wider ${m.bg} ${m.color}`}>{m.label}</span>;
}

function PrayerBadge({ status }) {
  const map = {
    new: { bg: "bg-red-50", color: "text-red-700", label: "New" },
    responded: { bg: "bg-green-50", color: "text-green-700", label: "Responded" },
    archived: { bg: "bg-cream-100", color: "text-navy/60", label: "Archived" },
  };
  const m = map[status] || map.new;
  return <span className={`px-2 py-0.5 rounded-full text-[0.65rem] uppercase font-semibold tracking-wider ${m.bg} ${m.color}`}>{m.label}</span>;
}

function Th({ children }) {
  return <th className="px-5 py-3 text-xs uppercase tracking-wider text-navy/60 font-semibold text-left">{children}</th>;
}

function Table({ columns, rows, empty }) {
  if (!rows.length) return (
    <section className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
      <p className="text-center text-navy/55 py-16">{empty}</p>
    </section>
  );
  return (
    <section className="bg-white rounded-2xl border border-cream-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="admin-table">
          <thead className="bg-cream-50 border-b border-cream-200">
            <tr>{columns.map((c) => <Th key={c}>{c}</Th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-cream-100 align-top hover:bg-cream-50">
                {r.map((cell, j) => <td key={j} className="px-5 py-4 text-navy/85">{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
